import React, { useEffect, useContext } from "react";
import { parse } from "query-string";
import { useHistory, useLocation } from "react-router-dom";
import { useLazyQuery } from "@apollo/react-hooks";

// auth
import { useAuth } from "frontend/hooks/useAuth";

// queries
import { GET_LATEST_PURCHASE } from "photos/queries";

// Helpers
import { getValidKey } from "photos/helpers";

// AssetDownloadContext
import AssetDownloadContext from "core/AssetDownload/Context";
import {
    setIsReadyForDownload,
    setPurchaseType,
    setShowPurchaseTypeDrawer,
    setDownloadResourceParams,
    setAttributionRequired
} from "core/AssetDownload/Context/actions";

// styles
import colors from "core/colors";
import { RootWrapperStyled, GridContainerStyled } from "./styles";

const RootWrapper = ({ children }) => {
    const { isLoggedIn } = useAuth();
    const queryString = useLocation().search;
    let history = useHistory();
    const { dispatch, state } = useContext(AssetDownloadContext);

    const { asset, purchaseType, assetDownloadConfig } = state;

    const {
        getDownloadResourceParams,
        getPurchaseTypeByKey,
        getPurchaseTypeByResolution,
        purchaseTypeKeys,
        atrributionRequiredCheck
    } = assetDownloadConfig;

    /*---------------------------
    | On Mount // First Load
    ---------------------------*/
    useEffect(() => {
        // To mitigate users bookmarking steps 2 & 3, we remove the query from the URL.
        // We hold their choice in state
        // this does not persist, so if user refreshes they will lose their choice.
        const { purchase: showPurchased, options: showOptions } = parse(
            queryString
        );

        if (showPurchased) {
            const purchaseKey =
                showPurchased === "full_res"
                    ? purchaseTypeKeys.rullRes
                    : purchaseTypeKeys.large;
            dispatch(setPurchaseType(getPurchaseTypeByKey(purchaseKey)));
            dispatch(setIsReadyForDownload(true));
        }
        if (showOptions) {
            dispatch(setShowPurchaseTypeDrawer(true));
        }

        if (showPurchased || showOptions) {
            history.replace(location.pathname);
        }
    }, []);

    /*---------------------------
    | isLoggedIn and Public Domain, should be ready to download
    ---------------------------*/
    useEffect(() => {
        // if public domain, and user is logged in, show ready for download
        if (isLoggedIn && purchaseType.key === purchaseTypeKeys.public) {
            dispatch(setIsReadyForDownload(true));
        }
    }, [isLoggedIn]);

    /*---------------------------
    | Update downloadResourceParams By purchaseType,
        should the user change the type
        or purchases through checkout
    ---------------------------*/
    useEffect(() => {
        dispatch(
            setDownloadResourceParams(getDownloadResourceParams(purchaseType))
        );
        dispatch(
            setAttributionRequired(atrributionRequiredCheck(purchaseType))
        );
    }, [purchaseType]);

    /*---------------------------
    | Purchase Data && Purchase Type
        // TODO: ICONS: This may be Photo Specific
        May need to alter to support both, or raise to consumer
        for unique implementations
    ---------------------------*/
    const [loadPurchase, { called, purchaseData }] = useLazyQuery(
        GET_LATEST_PURCHASE, // photo specific query
        {
            fetchPolicy: "network-only",
            onCompleted: () => {
                if (purchaseData && purchaseData.latestPurchase) {
                    const {
                        latestPurchase: { purchaseType }
                    } = purchaseData;

                    dispatch(
                        setPurchaseType(
                            getPurchaseTypeByResolution(purchaseType)
                        )
                    );
                }
            }
        }
    );

    useEffect(() => {
        // if purchaseType is non-paid,
        // we should check if they came from checkout
        // and paid for this asset
        if (purchaseType.id !== 3 && !called) {
            loadPurchase({
                variables: {
                    photoId: asset.id // photo specific param
                }
            });
        }
    }, [purchaseType, called, loadPurchase]);

    return (
        <RootWrapperStyled bgColor={colors.secondary.orca}>
            <GridContainerStyled
                mobileSpacing={0}
                tabletSpacing={16}
                desktopSpacing={55}
            >
                {children}
            </GridContainerStyled>
        </RootWrapperStyled>
    );
};

export default RootWrapper;
